// Piggington's Arcade — Snack Attack (R5). Pure game logic only: the stage table, spawn
// probabilities, the catch/miss reducers, warm-up question sequencing, and the localStorage
// key builders. Everything here is a plain function over plain data — no React, no
// components, no DOM — so tests/specs/16-snack-attack.spec.js can import this module
// directly and assert the whole rulebook deterministically with a seeded RNG.
//
// Law 1 posture (see PIGGINGTONS_ARCADE_SCOPE.md §1): this game pays NOTHING — no tokens,
// no Oinks, no OP — and writes NOTHING to household state. Its only persistence is two
// device-local localStorage keys (personal best, warm-up date), built by the helpers at the
// bottom. Nothing in this module (or in SnackCatch.jsx, which consumes it) may ever import
// or touch addTransaction or any request/earning path.

import { AGE_UP_THRESHOLD } from "./age.js";
import { DEFAULT_TT_TABLES, effectiveMathsBandOrder, generateMathsQuestion, generateTTQuestion, resolveMathsBand } from "./maths.js";

/* ------------------------------ seeded randomness ------------------------------ */

// Local copies of hashStr/mulberry32 (identical to lib/dailyQuestion.js's) rather than an
// import — decision §0.8 of the scope: duplicate locally now, hoist both callers to a shared
// rng.js as its own standalone cleanup later. Keeps this module's import surface down to
// maths.js only (lib→lib, same precedent as dailyQuestion.js).

export function hashStr(s) {
  let hash = 0;
  const str = String(s || "");
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(hash);
}

export function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ------------------------------ modes ------------------------------ */

// Gentle vs Standard splits at the unified AGE_UP_THRESHOLD (8) — decision §0.7 originally
// pinned this to 7 to match the Piggles/Piggington naming split, and the Aug 2026
// consolidation moved BOTH (plus the colour theme, previously 9) to a single switch at 8,
// preserving §0.7's real intent: this always matches the naming split. Callers pass
// effectiveAge(child) (lib/age.js). Same game, same stages; only forgiveness differs. Ages
// missing entirely (preview profiles, defensive rule) resolve to Standard.
export function modeForAge(age) {
  return typeof age === "number" && age < AGE_UP_THRESHOLD ? "gentle" : "standard";
}

// Gentle tuning: slower base fall, lower nasty-object caps, wider pig hitbox, and no
// penalty for missed good food (see applyMissedGood). Tunable after feel-testing, like
// the stage table itself.
export const GENTLE_SPEED_FACTOR = 0.78;
export const GENTLE_ROTTEN_CAP = 0.10;
export const GENTLE_HAZARD_CAP = 0.12;
export const GENTLE_HITBOX_FACTOR = 1.18; // vs 1.0 in Standard — applied by the renderer

/* ------------------------------ objects & scoring ------------------------------ */

// The nine falling objects (sprites live inline in SnackCatch.jsx, drawn from the approved
// art/snack-attack-objects.svg plus two hand-drawn additions — sa-tinhat, sa-bandage — added
// for the Aug 2026 player-feedback round). Two-tier nastiness is deliberate and load-bearing:
// rotten food punishes the SCORE (−2 Standard / −1 Gentle, breaks the combo, never a
// heart); boots and cans punish SURVIVAL (−1 heart, breaks the combo, never the score).
// The differing feedback teaches the distinction in one run.
//
// The two rare pickups (feedback round, Aug 2026):
//   tinhat  (cls "shield")  — worn until the next hazard connects, which pops it off instead
//                             of costing a heart. Only ONE can be worn; catching a second
//                             while already shielded pays a small bonus instead of stacking.
//                             Deliberately hazard-only — rotten food isn't a "hit", so the
//                             shield doesn't touch it (feedback-session decision).
//   bandage (cls "extra")  — +1 heart, capped at STARTING_HEARTS. A no-op (with its own
//                             friendly "already full" feedback) if already at max.
// Both are rarer than the acorn — see STAGES below — so a run's core rhythm stays
// food-in/junk-out; these are a treat within a treat.
export const OBJECT_KINDS = {
  apple: { cls: "good", symbol: "sa-apple", points: 1, speedFactor: 1 },
  carrot: { cls: "good", symbol: "sa-carrot", points: 1, speedFactor: 1 },
  cupcake: { cls: "treat", symbol: "sa-cupcake", points: 3, speedFactor: 1.15 },
  acorn: { cls: "treat", symbol: "sa-acorn", points: 5, speedFactor: 1.3 },
  rotten: { cls: "rotten", symbol: "sa-rotten", points: 0, speedFactor: 1 },
  boot: { cls: "hazard", symbol: "sa-boot", points: 0, speedFactor: 1 },
  can: { cls: "hazard", symbol: "sa-can", points: 0, speedFactor: 1 },
  tinhat: { cls: "shield", symbol: "sa-tinhat", points: 0, speedFactor: 1.1 },
  bandage: { cls: "extra", symbol: "sa-bandage", points: 0, speedFactor: 1.1 },
  // Crow Wave drops (GREEDY_CROW_SCOPE.md §0.8/§4, sprite handoff Aug 2026). These seven
  // exist ONLY in the crow's weighted drop tables below — pickSpawnKind never returns them,
  // so ordinary stages are untouched. Point values are the scope's §0.3 rebalance, NOT the
  // handoff's originals (no negative points ever — hazards cost hearts, exactly like
  // boot/can; scope §0.2). LB-329 (Alan, 17 Sep 2026): the crow WEARS a top hat now, and
  // tophat is what comes off him on defeat — the single biggest catch in the game, at the
  // points and speed the crow's hat always had, reachable ONLY via the defeat drop. The flat
  // cap stays as a treat in its own right: it moved into the ordinary treat pool
  // (pickSpawnKind) at its unchanged values, since the defeat drop was its only route before.
  welly: { cls: "hazard", symbol: "sa-welly", points: 0, speedFactor: 1 },
  sock: { cls: "hazard", symbol: "sa-sock", points: 0, speedFactor: 0.85 },
  cowpat: { cls: "hazard", symbol: "sa-cowpat", points: 0, speedFactor: 1.1 },
  anvilsilver: { cls: "hazard", symbol: "sa-anvil-silver", points: 0, speedFactor: 1.45 },
  duck: { cls: "treat", symbol: "sa-duck", points: 3, speedFactor: 0.8 },
  doughnut: { cls: "treat", symbol: "sa-doughnut", points: 3, speedFactor: 1 },
  flatcap: { cls: "treat", symbol: "sa-flat-cap", points: 10, speedFactor: 0.9 },
  tophat: { cls: "treat", symbol: "sa-top-hat", points: 10, speedFactor: 0.9 },
};

// Bonus paid when a tin hat is caught while one is already worn, instead of wasting the
// catch by discarding it silently.
export const SHIELD_ALREADY_WORN_BONUS = 2;

export const STARTING_HEARTS = 3;
export const COMBO_BONUS_EVERY = 10; // every 10 consecutive good/treat catches...
export const COMBO_BONUS_POINTS = 5; // ...pays +5 on top

export function rottenPenalty(mode) {
  return mode === "gentle" ? 1 : 2;
}

/* ------------------------------ stage table ------------------------------ */

// Signed-off starting values (scope §4.4) — explicitly tunable after feel-testing, which is
// why every number lives in this one exported table rather than scattered through the loop.
// "Stage" is a displayed difficulty level advancing on score thresholds; remaining
// probability mass after rotten/hazard/treat/acorn/shield/extra = common food (apple/carrot).
// shield (tin hat) and extra (bandage) are new (Aug 2026 feedback round) — deliberately
// rarer than the acorn at every stage, and extra rarer again than shield, so a run stays
// mostly about the core food/junk rhythm.
export const STAGES = [
  { enter: 0, speed: 1.0, spawnMs: 1300, rotten: 0.04, hazard: 0.04, treat: 0.10, acorn: 0.03, shield: 0.015, extra: 0.008 },
  { enter: 15, speed: 1.12, spawnMs: 1150, rotten: 0.06, hazard: 0.07, treat: 0.11, acorn: 0.04, shield: 0.020, extra: 0.010 },
  { enter: 35, speed: 1.25, spawnMs: 1000, rotten: 0.08, hazard: 0.10, treat: 0.12, acorn: 0.05, shield: 0.025, extra: 0.012 },
  { enter: 60, speed: 1.40, spawnMs: 880, rotten: 0.10, hazard: 0.13, treat: 0.13, acorn: 0.06, shield: 0.030, extra: 0.015 },
  { enter: 90, speed: 1.55, spawnMs: 780, rotten: 0.12, hazard: 0.16, treat: 0.14, acorn: 0.07, shield: 0.035, extra: 0.018 },
  { enter: 130, speed: 1.70, spawnMs: 700, rotten: 0.13, hazard: 0.18, treat: 0.15, acorn: 0.08, shield: 0.040, extra: 0.020 },
];

// Stage 7+ ramp: +50 score per stage, +0.12 speed per stage (capped), −40ms spawn per
// stage (floored), probabilities climbing toward their caps (shield/extra climb slower than
// the rest — a fixed fraction of the standard probStep — since rarity is the whole point).
export const STAGE_RAMP = {
  enterStep: 50,
  speedStep: 0.12, speedCap: 2.3,
  spawnStep: 40, spawnFloor: 520,
  probStep: 0.01,
  rottenCap: 0.15, hazardCap: 0.22, treatCap: 0.16, acornCap: 0.10,
  shieldStep: 0.004, shieldCap: 0.055,
  extraStep: 0.002, extraCap: 0.028,
};

// 1-based stage number for a score — unbounded (stage 7, 8, 9… as the +50/stage ramp
// continues), so an endless run keeps levelling forever even though its parameters cap.
export function stageForScore(score) {
  const s = Math.max(0, score | 0);
  const lastFixed = STAGES[STAGES.length - 1];
  if (s < lastFixed.enter) {
    for (let i = STAGES.length - 1; i >= 0; i--) {
      if (s >= STAGES[i].enter) return i + 1;
    }
    return 1;
  }
  return STAGES.length + Math.floor((s - lastFixed.enter) / STAGE_RAMP.enterStep);
}

// Resolved parameters for a stage in a mode: speed multiplier, spawn interval, and the four
// special-object probabilities. Gentle applies its slower base speed and lower nasty caps
// here, so every consumer (spawner, renderer, tests) sees one consistent set of numbers.
export function stageParams(stageNum, mode) {
  const n = Math.max(1, stageNum | 0);
  let p;
  if (n <= STAGES.length) {
    p = { ...STAGES[n - 1] };
  } else {
    const extra = n - STAGES.length;
    const base = STAGES[STAGES.length - 1];
    p = {
      enter: base.enter + extra * STAGE_RAMP.enterStep,
      speed: Math.min(STAGE_RAMP.speedCap, base.speed + extra * STAGE_RAMP.speedStep),
      spawnMs: Math.max(STAGE_RAMP.spawnFloor, base.spawnMs - extra * STAGE_RAMP.spawnStep),
      rotten: Math.min(STAGE_RAMP.rottenCap, base.rotten + extra * STAGE_RAMP.probStep),
      hazard: Math.min(STAGE_RAMP.hazardCap, base.hazard + extra * STAGE_RAMP.probStep),
      treat: Math.min(STAGE_RAMP.treatCap, base.treat + extra * STAGE_RAMP.probStep),
      acorn: Math.min(STAGE_RAMP.acornCap, base.acorn + extra * STAGE_RAMP.probStep),
      shield: Math.min(STAGE_RAMP.shieldCap, base.shield + extra * STAGE_RAMP.shieldStep),
      extra: Math.min(STAGE_RAMP.extraCap, base.extra + extra * STAGE_RAMP.extraStep),
    };
  }
  if (mode === "gentle") {
    p.speed = p.speed * GENTLE_SPEED_FACTOR;
    p.rotten = Math.min(p.rotten, GENTLE_ROTTEN_CAP);
    p.hazard = Math.min(p.hazard, GENTLE_HAZARD_CAP);
  }
  return p;
}

/* ------------------------------ spawner ------------------------------ */

// Picks which object kind falls next, from one rng draw against the stage's probability
// bands (bandage, then tin hat — rarest first — then acorn, then cupcake, then rotten, then
// a hazard — boot/can 50:50 — then common food, apple/carrot 50:50, taking all remaining
// mass). rng is any () => [0,1) function: Math.random in the live game, a seeded mulberry32
// in tests.
export function pickSpawnKind(rng, params) {
  const r = rng();
  let edge = params.extra;
  if (r < edge) return "bandage";
  edge += params.shield;
  if (r < edge) return "tinhat";
  edge += params.acorn;
  if (r < edge) return "acorn";
  edge += params.treat;
  // LB-329: the flat cap is an ordinary treat now — the top tenth of the treat band, so the
  // 10-point cap stays rarer than the 5-point acorn; the rest of the band is the cupcake.
  if (r < edge) return r >= edge - params.treat * 0.1 ? "flatcap" : "cupcake";
  edge += params.rotten;
  if (r < edge) return "rotten";
  edge += params.hazard;
  if (r < edge) return rng() < 0.5 ? "boot" : "can";
  return rng() < 0.5 ? "apple" : "carrot";
}

/* ------------------------------ run reducers ------------------------------ */

export function initialRunState() {
  return { score: 0, hearts: STARTING_HEARTS, combo: 0, stage: 1, shielded: false };
}

// The single scoring reducer, applied on every catch. Pure: returns a fresh state plus an
// event describing what happened, for the renderer to turn into floats/expressions.
//   good/treat caught  → +points, combo advances (every 10th consecutive pays +5 on top)
//   rotten caught      → score −2 (Gentle −1), floored at 0, combo breaks, hearts untouched
//   tinhat caught      → wears a shield (max 1); a second one while worn pays a small bonus
//                        instead of stacking. Doesn't touch the combo either way — it's a
//                        pickup, not food.
//   bandage caught     → +1 heart, capped at STARTING_HEARTS; a no-op (own event type) if
//                        already full. Doesn't touch the combo.
//   hazard caught      → normally −1 heart, combo breaks, score untouched. If a shield is
//                        worn, the hazard pops it off instead: no heart lost, but the combo
//                        still breaks — the hit still happened, it just didn't cost a life
//                        (feedback-session decision; deliberately hazard-only — rotten food
//                        isn't a "hit" and passes straight through a shield untouched).
export function applyCatch(state, kindId, mode) {
  const kind = OBJECT_KINDS[kindId];
  if (!kind) return { next: state, event: { type: "none" } };
  if (kind.cls === "good" || kind.cls === "treat") {
    const combo = state.combo + 1;
    const comboBonus = combo % COMBO_BONUS_EVERY === 0 ? COMBO_BONUS_POINTS : 0;
    const points = kind.points + comboBonus;
    const score = state.score + points;
    return {
      next: { ...state, score, combo, stage: stageForScore(score) },
      event: { type: kind.cls, kindId, points: kind.points, comboBonus },
    };
  }
  if (kind.cls === "rotten") {
    const penalty = rottenPenalty(mode);
    const score = Math.max(0, state.score - penalty);
    return {
      next: { ...state, score, combo: 0 },
      event: { type: "rotten", kindId, points: -penalty },
    };
  }
  if (kind.cls === "shield") {
    if (state.shielded) {
      const score = state.score + SHIELD_ALREADY_WORN_BONUS;
      return {
        next: { ...state, score, stage: stageForScore(score) },
        event: { type: "shieldBonus", points: SHIELD_ALREADY_WORN_BONUS },
      };
    }
    return { next: { ...state, shielded: true }, event: { type: "shield" } };
  }
  if (kind.cls === "extra") {
    if (state.hearts >= STARTING_HEARTS) return { next: state, event: { type: "extraFull" } };
    return { next: { ...state, hearts: state.hearts + 1 }, event: { type: "extra" } };
  }
  // hazard
  if (state.shielded) {
    return {
      next: { ...state, shielded: false, combo: 0 },
      event: { type: "shieldBroken", kindId },
    };
  }
  return {
    next: { ...state, hearts: state.hearts - 1, combo: 0 },
    event: { type: "hazard", kindId },
  };
}

// Missed good/treat food reaching the floor: no penalty in Gentle; −1 heart (and a broken
// combo) in Standard. Missed rotten food and hazards are never penalised in either mode —
// dodging them is the point. Missed tin hats/bandages are never penalised either — they're
// optional pickups, not obligations.
export function applyMissedGood(state, mode) {
  if (mode === "gentle") return { next: state, event: { type: "none" } };
  return {
    next: { ...state, hearts: state.hearts - 1, combo: 0 },
    event: { type: "missedGood" },
  };
}

/* ------------------------------ Crow Wave (Boss v2) ------------------------------ */

// Every 3rd stage from stage 4 onward (4, 7, 10, 13…) — trigger unchanged from Boss v1 —
// the Greedy Crow flies in and, for a fixed stretch of time, replaces the random spawner
// with his own drops, released from HIS position as he patrols. Mostly bad objects (Alan's
// directive, GREEDY_CROW_SCOPE.md): the job flips from catching to dodging. Outlast the
// timer and he's defeated — he flees and drops his top hat as a one-off prize (LB-329).
//
// Everything here is pure and deterministic given (t, width, rng), so the whole wave —
// patrol path, drop weights, timers, defeat rules — is unit-testable without the rAF loop.
// The v1 fixed-pattern gauntlet (BOSS_PATTERN_*) is deleted, superseded by these tables
// (scope §5); the clear-condition rules below survive from v1 unchanged.
export const BOSS_STAGE_START = 4;
export const BOSS_STAGE_INTERVAL = 3;

export function isBossStage(stageNum) {
  const n = stageNum | 0;
  return n >= BOSS_STAGE_START && (n - BOSS_STAGE_START) % BOSS_STAGE_INTERVAL === 0;
}

// Wave length and drop cadence. Gentle: shorter wave, slower drops (scope §2/§4) — same
// spectacle, less pressure.
export const CROW_WAVE_MS = { standard: 12000, gentle: 9000 };
export const CROW_DROP_MS = { standard: 900, gentle: 1100 };
// Squawk-telegraph before every drop (handoff's readable-tell rule): the crow opens his
// beak this long before the object actually spawns.
export const CROW_TELEGRAPH_MS = 200;
// The defeat drop — the crow's own top hat, the one he wears (LB-329). Never appears in any
// drop table and never leaves pickSpawnKind; this constant is its only route into a run.
export const CROW_HAT_KIND = "tophat";

// Weighted drop tables, [kindId, weight] with weights summing to 100. Standard: 70% hazard
// / 15% rotten / 15% treat — dodging is the mode, but greed stays tempting. Gentle
// ("defanged", scope §7.2 resolved 18 Aug 2026): identical spectacle, but only the slow
// welly ever costs a heart — the rest is rotten (score-only) and treats — on the shorter
// timer above.
export const CROW_DROP_TABLE_STANDARD = [
  ["welly", 20], ["sock", 15], ["cowpat", 15], ["anvilsilver", 10], ["boot", 5], ["can", 5],
  ["rotten", 15],
  ["duck", 7], ["doughnut", 8],
];
export const CROW_DROP_TABLE_GENTLE = [
  ["welly", 20],
  ["rotten", 45],
  ["duck", 17], ["doughnut", 18],
];

export function crowDropTableFor(mode) {
  return mode === "gentle" ? CROW_DROP_TABLE_GENTLE : CROW_DROP_TABLE_STANDARD;
}

// One weighted draw from a drop table. rng is any () => [0,1): Math.random live, seeded
// mulberry32 in tests.
export function pickCrowDrop(rng, table) {
  const total = table.reduce((sum, [, w]) => sum + w, 0);
  let r = rng() * total;
  for (const [kindId, w] of table) {
    r -= w;
    if (r < 0) return kindId;
  }
  return table[table.length - 1][0];
}

// Patrol speed in px/s, scaling gently with which boss this is (stage 4 = 1st crow, stage 7
// = 2nd…) — same values-table pattern as STAGE_RAMP.
export const CROW_PATROL = { baseSpeed: 110, perBossSpeed: 14, speedCap: 190 };
// Vertical bob: a sine layered on the horizontal patrol (Alan's directive: up and down as
// well as across). Amplitude in px around the renderer's chosen centre line; period in ms.
export const CROW_BOB = { amplitudePx: 36, periodMs: 2400 };

// 1-based ordinal of a boss stage (stage 4 → 1, stage 7 → 2…); 0 for non-boss stages.
export function crowBossOrdinal(stageNum) {
  if (!isBossStage(stageNum)) return 0;
  return 1 + ((stageNum | 0) - BOSS_STAGE_START) / BOSS_STAGE_INTERVAL;
}

export function crowPatrolSpeed(stageNum) {
  const ord = Math.max(1, crowBossOrdinal(stageNum));
  return Math.min(CROW_PATROL.speedCap, CROW_PATROL.baseSpeed + (ord - 1) * CROW_PATROL.perBossSpeed);
}

// The crow's deterministic position at elapsed wave-time tMs, in a field `width` px wide
// for a crow rendered `crowW` px wide: a triangle-wave patrol between the margins (constant
// speed, clean edge reversals) plus the sine bob as a y-offset around the renderer's centre
// line. Returns { x, yOffset, dir } — dir is +1 moving right, −1 moving left, for sprite
// mirroring (he faces left natively; flip when flying right).
export function crowPosAt(tMs, width, crowW, speedPxPerSec) {
  const margin = crowW / 2;
  const span = Math.max(1, width - crowW);
  const dist = Math.max(0, tMs) * (speedPxPerSec / 1000);
  const phase = dist % (2 * span);
  const forward = phase < span;
  const x = margin + (forward ? phase : 2 * span - phase);
  const yOffset = CROW_BOB.amplitudePx * Math.sin((2 * Math.PI * tMs) / CROW_BOB.periodMs);
  return { x, yOffset, dir: forward ? 1 : -1 };
}

// "Wave clear" — survive the timer having lost at most 1 heart — pays a flat bonus on top
// of the always-dropped hat (scope §0.5's two tiers: survived = hat; survived WELL = hat +
// bonus + celebration). A heart a shield absorbed doesn't count as lost. Unchanged from v1.
export const BOSS_CLEAR_MAX_HEARTS_LOST = 1;
export const BOSS_CLEAR_BONUS_POINTS = 20;

export function bossWaveCleared(heartsLost) {
  return (heartsLost | 0) <= BOSS_CLEAR_MAX_HEARTS_LOST;
}

// `&scBoss=1` (preview-mode-only, enforced by the caller like scTest): deterministic short
// crow wave for the E2E spec — the run opens at stage 4 mid-wave on full hearts, every crow
// drop is forced to `dropKind` AND placed a full pig-width beside the stationary pig (a
// guaranteed, penalty-free miss — crow drops never punish the floor), the hat spawns at the
// PIG's x (auto-caught), and the spawner goes silent afterwards. Net: hearts never move, the
// clear branch is guaranteed, and the final score settles at exactly
// startScore + BOSS_CLEAR_BONUS_POINTS + the hat's 10 (60+20+10=90) with nothing random
// left for the spec to race.
export const SC_BOSS_TEST = { waveMs: 3000, dropMs: 700, startScore: 60, dropKind: "rotten" };

/* ------------------------------ Brain Warm-Up ------------------------------ */

// The daily gate: 3 correct answers IN TOTAL (not consecutive, unlimited attempts, no fail
// state — decision §0.11) unlocks the arcade until midnight, per device per child. Questions
// come from the existing Learn & Earn maths machinery so the gate works fully offline with
// zero parent-stocked content for every child: a 50:50 mix of TT facts (the child's
// configured tables, defaulting to DEFAULT_TT_TABLES) and Maths Workout questions ONE BAND
// BELOW the child's resolved band (warmupBandFor). Answers pay nothing and record nothing — generated, answered,
// discarded (component state only).
export const WARMUP_TARGET = 3;

// The warm-up's Maths Workout half sits one band below the child's own (Alan, 2 Oct: "some of
// the warm up questions are really hard... It's just a bit of fun and we don't want to put
// people off"). The child's band is pitched to stretch them in a Workout; a gate to a game
// should be quick. The lowest band has nothing below it and stays where it is. Uses the same
// band order the Workout does, so a published ladder with more rungs steps down one rung.
export function warmupBandFor(child, learning) {
  const band = resolveMathsBand(child, learning && learning.mathsBand);
  const order = effectiveMathsBandOrder();
  const i = order.indexOf(band);
  return i > 0 ? order[i - 1] : band;
}

export function warmupQuestion(child, learning) {
  const tables = learning && Array.isArray(learning.ttTables) && learning.ttTables.length > 0 ? learning.ttTables : DEFAULT_TT_TABLES;
  if (Math.random() < 0.5) return generateTTQuestion(tables);
  return generateMathsQuestion(warmupBandFor(child, learning));
}

// Deterministic warm-up question for the `&scTest=1` E2E hook (preview-mode-only): the spec
// imports this exact function and recomputes what the app will serve for each question
// index, so it can drive the keypad with known-correct (or known-wrong) answers. TT-only
// and independent of any learning config, so it never drifts with parent settings.
export function scTestWarmupQuestion(index) {
  const rng = mulberry32(hashStr(`sc-test|${index}`));
  const table = DEFAULT_TT_TABLES[Math.floor(rng() * DEFAULT_TT_TABLES.length)];
  const mult = 1 + Math.floor(rng() * 12);
  return { prompt: `${table} \u00d7 ${mult}`, answer: table * mult };
}

/* ------------------------------ settings accessors ------------------------------ */

// Parent controls live at learningContent[childId].arcade — written by LearningAdminTab's
// Arcade block, read here. Defensive throughout (the standing rule): a preview-mode or
// pre-migration profile may lack the object entirely, and absence must mean the defaults —
// arcade visible, Brain Warm-Up required.
export function arcadeEnabledFor(learning) {
  return !(learning && learning.arcade && learning.arcade.snackCatchEnabled === false);
}

export function arcadeWarmupModeFor(learning) {
  return learning && learning.arcade && learning.arcade.warmupMode === "free" ? "free" : "warmup";
}

/* ------------------------------ device-local persistence keys ------------------------------ */

// The game's ONLY persistence: a per-child personal best ({ best, at }) and the date the
// warm-up was last completed — both device-local by design (scope §5.2: a best that doesn't
// follow across devices is the accepted v1 cost; syncing is bundled into the fast-follow).
// Key prefix "lb-cc-" (from the feature's Coin Catcher working title) is kept as the stable
// storage namespace even though the shipped name is Snack Attack.
export function bestKeyFor(childId) {
  return `lb-cc-best-${childId}`;
}

export function warmupKeyFor(childId) {
  return `lb-cc-warmup-${childId}`;
}

// First-run drag-hint flag — per device, not per child (it teaches the input, not the child).
export const HINT_KEY = "lb-cc-hint";
